import {ASTNode} from 'notebook/tables/excess/AST';
import {FunctionEvalArgument} from 'notebook/tables/excess/FunctionConstants';
import {ArgTypeAssertion} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import {EvaluablePrimitive} from 'notebook/tables/typed-values/DataTypes';

type NonErroredArgument<T extends FunctionEvalArgument = FunctionEvalArgument> = Exclude<T, util.CellError>;

function throwUnless<TValue extends FunctionEvalArgument>(
  fn: (node: ASTNode, value: FunctionEvalArgument, context?: string) => value is TValue
): ArgTypeAssertion<NonErroredArgument<TValue>> {
  return (node, value, context) => {
    if (value instanceof util.CellError) {
      throw value;
    }
    if (!fn(node, value)) {
      throw new util.ValueError(`${context} has wrong type.`);
    }
  };
}

const singularValue: ArgTypeAssertion<EvaluablePrimitive> = throwUnless(
  (node, value): value is EvaluablePrimitive => node.isSingular()
);

const cellRange = throwUnless(
  (node, value): value is Exclude<NonErroredArgument, EvaluablePrimitive> => !node.isSingular()
);

const text = throwUnless((node, value): value is string => typeof value === 'string');

// TODO: Rename this to anyNotError
const any: ArgTypeAssertion<NonErroredArgument> = throwUnless(
  (node, value): value is NonErroredArgument => true
);

const number: ArgTypeAssertion<number> = throwUnless(
  (node, value): value is number => typeof value === 'number'
);

const boolean = throwUnless((node, value): value is boolean => typeof value === 'boolean');

function positiveNumber(node: ASTNode, value: EvaluablePrimitive, context?: string): asserts value is number {
  number(node, value, context);
  if (value <= 0) {
    throw new util.NumericalError(`${context} is ${value}. It should be greater than 0.`);
  }
}

function positiveInteger(
  node: ASTNode,
  value: EvaluablePrimitive,
  context?: string
): asserts value is number {
  positiveNumber(node, value, context);
  if (!Number.isInteger(value)) {
    throw new util.NumericalError(`${context} must be a positive integer.`);
  }
}

function nonnegativeNumber(
  node: ASTNode,
  value: EvaluablePrimitive,
  context?: string
): asserts value is number {
  number(node, value, context);
  if (value < 0) {
    throw new util.NumericalError(`${context} is ${value}. It should be greater than or equal to 0.`);
  }
}

/**
 * By default, any errors thrown during evaluation of a function's arguments will short-circuit evaluation of
 * the cell. This wrapper allows the function to instead opt-in to receive the error values as arguments.
 *
 * This only catches errors thrown while evaluating the argument. If the argument is evaluated successfully
 * but its type is incompatible with the parameter type, the typecheck will still fail and throw a CellError.
 *
 * This does not allow the function to *return* an error value. Any function that declares arguments with this
 * wrapper is expected to check the arguments for errors and handle or rethrow them.
 */
export function withErrorsAllowed<TValue extends FunctionEvalArgument>(
  assertion: ArgTypeAssertion<NonErroredArgument<TValue>>
): ArgTypeAssertion<TValue | util.CellError> {
  return (node, value, context) => {
    if (value instanceof util.CellError) {
      return;
    }
    assertion(node, value, context);
  };
}

export default {
  any,
  cellRange,
  number,
  positiveNumber,
  nonnegativeNumber,
  positiveInteger,
  text,
  singularValue,
  boolean,
};
